import { useEffect, useState } from 'react';
import { api } from '../../../lib/api.js';
import ScheduleSendModal from './modals/ScheduleSendModal.jsx';

// Step 4 of 4: inline email preview. Calls POST /api/editions/:date/render,
// puts the returned HTML in an iframe srcDoc so the subscriber view is exact.
// Subject line + preview text are editable here (last chance before send).
//
// Send action: "Schedule…" opens ScheduleSendModal which calls back with
// mode='schedule' + scheduledFor (ISO 8601) via the `onSend` prop. (Immediate
// send-now was removed — it didn't reliably mark the edition sent in Beehiiv.)
export default function InlineEmailPreview({
  isoDate,
  edition,
  onBack,
  onSend,
  onChangeEditionField,
}) {
  const [html, setHtml] = useState('');
  const [subjectLine, setSubjectLine] = useState(edition?.subjectLine || '');
  const [previewText, setPreviewText] = useState(edition?.previewText || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .post(`/editions/${isoDate}/render`, {})
      .then((res) => {
        if (cancelled) return;
        setHtml(res.html || '');
        if (!subjectLine && res.subjectLine) setSubjectLine(res.subjectLine);
        if (!previewText && res.previewText) setPreviewText(res.previewText);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isoDate]); // eslint-disable-line react-hooks/exhaustive-deps

  function commitSubject() {
    if (subjectLine !== edition?.subjectLine) {
      onChangeEditionField?.('subjectLine', subjectLine);
    }
  }
  function commitPreview() {
    if (previewText !== edition?.previewText) {
      onChangeEditionField?.('previewText', previewText);
    }
  }

  // Force-commit the in-component subject + preview to the parent's edition
  // patch before send. Otherwise an editor who types and clicks Send without
  // first blurring the input ends up sending the OLD value (the on-blur
  // handler never fires). The server then rejects with "missing subjectLine".
  function flushPendingFields() {
    if (!onChangeEditionField) return;
    if (subjectLine !== (edition?.subjectLine || '')) {
      onChangeEditionField('subjectLine', subjectLine);
    }
    if (previewText !== (edition?.previewText || '')) {
      onChangeEditionField('previewText', previewText);
    }
  }

  // Called from ScheduleSendModal once the editor confirms a future time.
  // Re-throws so the modal can keep itself open + surface the error (and, when
  // the edition was already pushed to Beehiiv, prompt to schedule anyway with
  // force=true). `opts.force` overrides the server's re-publish guard.
  async function handleSchedule(scheduledForIso, opts = {}) {
    flushPendingFields();
    await onSend?.({ mode: 'schedule', scheduledFor: scheduledForIso, force: opts.force });
    setShowScheduleModal(false);
  }

  // Required-field guards so the editor can't even click Send when subject or
  // preview is empty. Trimmed so a whitespace-only string counts as missing.
  const hasSubject = !!subjectLine.trim();
  const hasPreview = !!previewText.trim();
  const canSend = hasSubject && hasPreview;
  const missingFields = [
    !hasSubject && 'subject line',
    !hasPreview && 'preview text',
  ].filter(Boolean);

  return (
    <div className="space-y-4 max-w-[860px]">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
            Step 4 of 4 · Email preview
          </div>
          <h1 className="hxd-page-title">Final look</h1>
        </div>
        <button type="button" onClick={onBack} className="hxd-btn hxd-btn-ghost hxd-btn-sm">
          ← Back to images
        </button>
      </div>

      <section className="hxd-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="hxd-label flex items-center gap-1.5">
              Subject line
              <span className="text-hxd-orange" title="Required">*</span>
            </label>
            <input
              className={[
                'hxd-input font-semibold',
                !hasSubject ? '!border-hxd-orange' : '',
              ].join(' ')}
              value={subjectLine}
              onChange={(e) => setSubjectLine(e.target.value)}
              onBlur={commitSubject}
              maxLength={120}
              placeholder="Required — shows in subscribers' inboxes"
            />
            <div className="text-[11px] mt-1 flex items-center justify-between">
              <span className={!hasSubject ? 'text-hxd-orange font-semibold' : 'text-hxd-muted'}>
                {!hasSubject ? 'Required for send' : `${subjectLine.length}/120`}
              </span>
              {hasSubject && (
                <span className="text-hxd-muted">{subjectLine.length}/120</span>
              )}
            </div>
          </div>
          <div>
            <label className="hxd-label flex items-center gap-1.5">
              Preview text
              <span className="text-hxd-orange" title="Required">*</span>
            </label>
            <input
              className={[
                'hxd-input',
                !hasPreview ? '!border-hxd-orange' : '',
              ].join(' ')}
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              onBlur={commitPreview}
              maxLength={160}
              placeholder="Required — the inbox-preview snippet next to the subject"
            />
            <div className="text-[11px] mt-1 flex items-center justify-between">
              <span className={!hasPreview ? 'text-hxd-orange font-semibold' : 'text-hxd-muted'}>
                {!hasPreview ? 'Required for send' : `${previewText.length}/160`}
              </span>
              {hasPreview && (
                <span className="text-hxd-muted">{previewText.length}/160</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="hxd-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-[12px] text-hxd-muted">Rendering preview…</div>
        ) : error ? (
          <div className="p-4 text-[12px] text-hxd-err">Render failed: {error}</div>
        ) : (
          <iframe
            title="HXD email preview"
            srcDoc={html}
            sandbox=""
            className="w-full block bg-white"
            style={{ height: '1100px', border: 'none' }}
          />
        )}
      </section>

      <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-black/95 backdrop-blur border-t border-[#333] flex items-center gap-3 flex-wrap">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted">
          Step 4 of 4
        </div>
        {!canSend ? (
          <div className="text-[12px] text-hxd-orange font-semibold flex-1 min-w-0 mx-2 truncate">
            Fill {missingFields.join(' + ')} above to enable scheduling
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowScheduleModal(true)}
            disabled={loading || !!error || !canSend}
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
          >
            Schedule send →
          </button>
        </div>
      </div>

      {showScheduleModal && (
        <ScheduleSendModal
          isoDate={isoDate}
          onCancel={() => setShowScheduleModal(false)}
          onConfirm={handleSchedule}
        />
      )}
    </div>
  );
}
